import {
  ButtonBase,
  Grid,
  GridItem,
  Lightbox,
  Picture,
  Text,
  generateDesktopSourcesForPicture,
} from '@farmersdog/corgi-x';
import type { BenefitCardVariant } from '../../data';
import { benefitDataMap } from '../../data';

import CheckIcon from '../../assets/check-icon.svg';
import CloseIcon from '../../assets/close-icon.svg';

import styles from './BenefitModal.module.css';
import { paths } from '@farmersdog/constants';
import { ExtendedButton, ExtendedText } from '../../../../components';
import { CtaTrackerV2, HomepageRedesignSections } from '../../../CtaTrackerV2';

interface BenefitModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentKey: BenefitCardVariant;
}

export const BenefitModal = ({
  isOpen,
  onClose,
  contentKey,
}: BenefitModalProps) => {
  const { sources, title, benefits, testimonial } = benefitDataMap[contentKey];
  const desktopSources = generateDesktopSourcesForPicture(sources);

  return (
    <div>
      <Lightbox
        width="1210px"
        height="600px"
        isOpen={isOpen}
        backdropClassName={styles.backdrop}
        closeOnBackdropClick
        onClose={onClose}
      >
        <Grid>
          <GridItem lg={5} className={styles.imageColumn}>
            <Picture
              className={styles.image}
              sources={desktopSources}
              alt={title}
            />
          </GridItem>
          <GridItem lg={7}>
            <div className={styles.modalContent}>
              <ExtendedText
                bottomSpacing="lg"
                as="h3"
                variant="heading-28"
                bold
                color="kale-3"
                className={styles.title}
              >
                {title}
              </ExtendedText>
              <ul className={styles.list}>
                {benefits.map((benefit, index) => {
                  const isLastItem = index === benefits.length - 1;

                  return (
                    <li key={index}>
                      <CheckIcon />
                      <Text
                        bold
                        as="p"
                        color="kale-3"
                        topSpacing="none"
                        variant="heading-22"
                        bottomSpacing={isLastItem ? 'lg' : 'md'}
                      >
                        {benefit}
                      </Text>
                    </li>
                  );
                })}
              </ul>
              <Text
                bold
                as="p"
                topSpacing="none"
                bottomSpacing="xs"
                variant="heading-12"
                className={styles.quoteTitle}
              >
                Heard around the park
              </Text>
              <blockquote className={styles.blockquote}>
                <Text
                  as="p"
                  topSpacing="none"
                  bottomSpacing="sm"
                  variant="article-16"
                >
                  &quot;{testimonial.quote}&quot;
                </Text>
                <footer>
                  <Text bold variant="heading-16">
                    - {testimonial.author}
                  </Text>
                </footer>
              </blockquote>
              <CtaTrackerV2
                type="select"
                moduleLevel={2}
                moduleName={HomepageRedesignSections.LifetimeOfBenefits}
              >
                <ExtendedButton
                  type="link"
                  to={paths.PATH_SIGNUP}
                  variant="bordered-charcoal"
                  className={styles.button}
                >
                  Get Started
                </ExtendedButton>
              </CtaTrackerV2>
              <CtaTrackerV2
                type="exit"
                moduleLevel={2}
                moduleName={HomepageRedesignSections.LifetimeOfBenefits}
              >
                <ButtonBase className={styles.closeButton} onClick={onClose}>
                  <CloseIcon />
                </ButtonBase>
              </CtaTrackerV2>
            </div>
          </GridItem>
        </Grid>
      </Lightbox>
    </div>
  );
};
